import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
    Box,
    Button,
    Chip,
    Collapse,
    Container,
    IconButton,
    InputAdornment,
    Paper,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    Alert,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useQuery } from '@tanstack/react-query';
import apiService from '../services/api';
import { ROUTES_NAMES } from '../components/Layout';

const PLATFORM_LABEL: Record<string, string> = {
    meta: 'Meta',
    tiktok: 'TikTok',
    youtube: 'YouTube',
    spotify: 'Spotify',
};

function formatDate(iso?: string | null): string {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('es-ES');
    } catch {
        return '—';
    }
}

function formatDateTime(iso?: string | null): string {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('es-ES');
    } catch {
        return '—';
    }
}

function pipelineStatusColor(
    status: string,
): 'default' | 'success' | 'warning' | 'error' | 'info' {
    switch (status) {
        case 'ok':
            return 'success';
        case 'running':
            return 'info';
        case 'error':
            return 'error';
        case 'idle':
            return 'default';
        default:
            return 'warning';
    }
}

function CompanyPodcastsRow({
    podcasts,
}: {
    podcasts: Array<{
        podcastId: string;
        title: string;
        slug: string;
        on_prixi: boolean;
        prixi_company_id?: string;
        prixiPodcastId?: string;
        socialAccounts: Array<{ platform: string; username?: string }>;
    }>;
}) {
    if (podcasts.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1, px: 2 }}>
                Sin podcasts vinculados a Prixi
            </Typography>
        );
    }

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Podcast</TableCell>
                    <TableCell>Slug</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>prixi_company_id</TableCell>
                    <TableCell>prixiPodcastId</TableCell>
                    <TableCell>Conexiones</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {podcasts.map((p) => (
                    <TableRow key={p.podcastId}>
                        <TableCell>{p.title}</TableCell>
                        <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                                {p.slug}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Chip
                                size="small"
                                label={p.on_prixi ? 'on_prixi' : 'No activo'}
                                color={p.on_prixi ? 'success' : 'default'}
                            />
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                                {p.prixi_company_id ?? '—'}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2" fontFamily="monospace" sx={{ fontSize: '0.75rem' }}>
                                {p.prixiPodcastId ?? '—'}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            {p.socialAccounts.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    —
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {p.socialAccounts.map((sa) => (
                                        <Chip
                                            key={`${p.podcastId}-${sa.platform}`}
                                            size="small"
                                            variant="outlined"
                                            label={
                                                sa.username
                                                    ? `${PLATFORM_LABEL[sa.platform] ?? sa.platform}: ${sa.username}`
                                                    : PLATFORM_LABEL[sa.platform] ?? sa.platform
                                            }
                                        />
                                    ))}
                                </Box>
                            )}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function CompaniesTab() {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [, setSearchParams] = useSearchParams();

    const { data, isLoading, error } = useQuery({
        queryKey: ['admin-prixi-entities', page, rowsPerPage, search],
        queryFn: () => apiService.getAdminPrixiEntities(page + 1, rowsPerPage, search || undefined),
    });

    const items = data?.items ?? [];
    const total = data?.pagination?.total ?? 0;

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(0);
        setSearch(searchInput.trim());
    };

    const toggleExpanded = (companyId: string) => {
        setExpanded((prev) => ({ ...prev, [companyId]: !prev[companyId] }));
    };

    return (
        <>
            <Box component="form" onSubmit={handleSearchSubmit} sx={{ mb: 2, maxWidth: 480 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Buscar por empresa, podcast o ID Prixi"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton type="submit" edge="end" aria-label="buscar">
                                    <SearchIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    No se pudieron cargar las empresas Prixi
                </Alert>
            )}

            <Paper>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell width={48} />
                                        <TableCell>Empresa</TableCell>
                                        <TableCell>prixiCompanyId</TableCell>
                                        <TableCell>Email contacto</TableCell>
                                        <TableCell>Podcasts</TableCell>
                                        <TableCell>Creada</TableCell>
                                        <TableCell align="right">Acciones</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center">
                                                No hay empresas Prixi
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        items.map((row) => {
                                            const isOpen = expanded[row.companyId] ?? false;
                                            return (
                                                <React.Fragment key={row.companyId}>
                                                    <TableRow hover>
                                                        <TableCell>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => toggleExpanded(row.companyId)}
                                                                aria-label={isOpen ? 'contraer' : 'expandir'}
                                                            >
                                                                {isOpen ? (
                                                                    <KeyboardArrowUpIcon />
                                                                ) : (
                                                                    <KeyboardArrowDownIcon />
                                                                )}
                                                            </IconButton>
                                                        </TableCell>
                                                        <TableCell>{row.companyName}</TableCell>
                                                        <TableCell>
                                                            <Typography
                                                                variant="body2"
                                                                fontFamily="monospace"
                                                                sx={{ fontSize: '0.75rem' }}
                                                            >
                                                                {row.prixiCompanyId}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>{row.prixiContactEmail ?? '—'}</TableCell>
                                                        <TableCell>{row.podcasts.length}</TableCell>
                                                        <TableCell>{formatDate(row.createdAt)}</TableCell>
                                                        <TableCell align="right">
                                                            <Button
                                                                size="small"
                                                                sx={{ mr: 1 }}
                                                                onClick={() =>
                                                                    setSearchParams({
                                                                        tab: 'campaigns',
                                                                        companyId: row.companyId,
                                                                    })
                                                                }
                                                            >
                                                                Campañas
                                                            </Button>
                                                            <Button
                                                                component={RouterLink}
                                                                to={`/admin/companies/${row.companyId}`}
                                                                size="small"
                                                                endIcon={<OpenInNewIcon />}
                                                            >
                                                                Config
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell
                                                            colSpan={7}
                                                            sx={{ py: 0, borderBottom: isOpen ? undefined : 0 }}
                                                        >
                                                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                                <Box sx={{ py: 1, pl: 6, pr: 2 }}>
                                                                    <CompanyPodcastsRow podcasts={row.podcasts} />
                                                                </Box>
                                                            </Collapse>
                                                        </TableCell>
                                                    </TableRow>
                                                </React.Fragment>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            labelRowsPerPage="Filas por página"
                        />
                    </>
                )}
            </Paper>
        </>
    );
}

function CampaignsTab() {
    const [searchParams, setSearchParams] = useSearchParams();
    const companyIdFilter = searchParams.get('companyId') || undefined;
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [copiedId, setCopiedId] = useState<string | null>(null);

    const { data, isLoading, error } = useQuery({
        queryKey: ['admin-prixi-campaigns', page, rowsPerPage, search, companyIdFilter],
        queryFn: () =>
            apiService.getAdminPrixiCampaigns({
                page: page + 1,
                limit: rowsPerPage,
                search: search || undefined,
                companyId: companyIdFilter,
            }),
    });

    const items = data?.items ?? [];
    const total = data?.pagination?.total ?? 0;

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(0);
        setSearch(searchInput.trim());
    };

    const clearCompanyFilter = () => {
        const next = new URLSearchParams(searchParams);
        next.delete('companyId');
        next.set('tab', 'campaigns');
        setSearchParams(next);
        setPage(0);
    };

    const copyHint = async (id: string, hint: string) => {
        try {
            await navigator.clipboard.writeText(hint);
            setCopiedId(id);
            window.setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1500);
        } catch {
            // ignore
        }
    };

    return (
        <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Campañas sincronizadas desde Prixi (<code>prixi_campaigns</code>). Pipeline:{' '}
                <code>yarn company:mentions:prixi-campaigns --campaign-id …</code>
            </Typography>

            {companyIdFilter && (
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    action={
                        <Button color="inherit" size="small" onClick={clearCompanyFilter}>
                            Quitar filtro
                        </Button>
                    }
                >
                    Filtrando por empresa <code>{companyIdFilter}</code>
                </Alert>
            )}

            <Box component="form" onSubmit={handleSearchSubmit} sx={{ mb: 2, maxWidth: 480 }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Buscar por nombre, label o ID"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton type="submit" edge="end" aria-label="buscar">
                                    <SearchIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    No se pudieron cargar las campañas Prixi
                </Alert>
            )}

            <Paper>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell width={48} />
                                        <TableCell>Campaña</TableCell>
                                        <TableCell>Empresa</TableCell>
                                        <TableCell>Marca</TableCell>
                                        <TableCell>Periodo</TableCell>
                                        <TableCell>Scopes</TableCell>
                                        <TableCell>Estado</TableCell>
                                        <TableCell>Último run</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {items.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} align="center">
                                                No hay campañas Prixi sincronizadas
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        items.map((row) => {
                                            const isOpen = expanded[row.id] ?? false;
                                            return (
                                                <React.Fragment key={row.id}>
                                                    <TableRow hover>
                                                        <TableCell>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() =>
                                                                    setExpanded((prev) => ({
                                                                        ...prev,
                                                                        [row.id]: !prev[row.id],
                                                                    }))
                                                                }
                                                            >
                                                                {isOpen ? (
                                                                    <KeyboardArrowUpIcon />
                                                                ) : (
                                                                    <KeyboardArrowDownIcon />
                                                                )}
                                                            </IconButton>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" fontWeight={600}>
                                                                {row.name || row.label || '—'}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                fontFamily="monospace"
                                                                color="text.secondary"
                                                            >
                                                                {row.prixiCampaignId}
                                                            </Typography>
                                                            {!row.enabled && (
                                                                <Chip
                                                                    size="small"
                                                                    label="disabled"
                                                                    sx={{ ml: 1 }}
                                                                />
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {row.companyName ?? row.podscopeCompanyId}
                                                        </TableCell>
                                                        <TableCell>
                                                            {row.brandName ?? row.podscopeBrandId}
                                                        </TableCell>
                                                        <TableCell>
                                                            {row.periodStart} → {row.periodEnd}
                                                        </TableCell>
                                                        <TableCell>
                                                            {row.scopeCount} podcast
                                                            {row.scopeCount === 1 ? '' : 's'} /{' '}
                                                            {row.programCount} prog.
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                size="small"
                                                                label={row.pipelineStatus}
                                                                color={pipelineStatusColor(row.pipelineStatus)}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {formatDateTime(row.lastPipelineRunAt)}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell
                                                            colSpan={8}
                                                            sx={{ py: 0, borderBottom: isOpen ? undefined : 0 }}
                                                        >
                                                            <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                                                <Box sx={{ py: 2, pl: 6, pr: 2 }}>
                                                                    {row.lastPipelineError && (
                                                                        <Alert severity="error" sx={{ mb: 2 }}>
                                                                            {row.lastPipelineError}
                                                                        </Alert>
                                                                    )}
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 1,
                                                                            mb: 2,
                                                                            flexWrap: 'wrap',
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            fontFamily="monospace"
                                                                            sx={{ fontSize: '0.8rem' }}
                                                                        >
                                                                            {row.pipelineHint}
                                                                        </Typography>
                                                                        <Tooltip
                                                                            title={
                                                                                copiedId === row.id
                                                                                    ? 'Copiado'
                                                                                    : 'Copiar CLI'
                                                                            }
                                                                        >
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={() =>
                                                                                    copyHint(
                                                                                        row.id,
                                                                                        row.pipelineHint,
                                                                                    )
                                                                                }
                                                                            >
                                                                                <ContentCopyIcon fontSize="small" />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </Box>
                                                                    <Table size="small">
                                                                        <TableHead>
                                                                            <TableRow>
                                                                                <TableCell>Podcast</TableCell>
                                                                                <TableCell>programIds</TableCell>
                                                                                <TableCell>brandIds</TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {row.scopes.map((s) => (
                                                                                <TableRow key={s.podcastId}>
                                                                                    <TableCell>
                                                                                        {s.podcastTitle ??
                                                                                            s.podcastId}
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        <Typography
                                                                                            variant="caption"
                                                                                            fontFamily="monospace"
                                                                                        >
                                                                                            {s.programIds.join(', ') ||
                                                                                                '—'}
                                                                                        </Typography>
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        <Typography
                                                                                            variant="caption"
                                                                                            fontFamily="monospace"
                                                                                        >
                                                                                            {s.brandIds.join(', ') ||
                                                                                                '—'}
                                                                                        </Typography>
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </TableBody>
                                                                    </Table>
                                                                </Box>
                                                            </Collapse>
                                                        </TableCell>
                                                    </TableRow>
                                                </React.Fragment>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            onPageChange={(_, p) => setPage(p)}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            labelRowsPerPage="Filas por página"
                        />
                    </>
                )}
            </Paper>
        </>
    );
}

const AdminPrixiCompanies: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = useMemo(() => {
        const t = searchParams.get('tab');
        return t === 'campaigns' ? 'campaigns' : 'companies';
    }, [searchParams]);

    const setTab = (next: 'companies' | 'campaigns') => {
        const params = new URLSearchParams(searchParams);
        if (next === 'companies') {
            params.delete('tab');
            params.delete('companyId');
        } else {
            params.set('tab', 'campaigns');
        }
        setSearchParams(params);
    };

    return (
        <Container maxWidth="lg">
            <Box sx={{ py: 4 }}>
                <Button component={RouterLink} to={ROUTES_NAMES.ADMIN} size="small" sx={{ mb: 2 }}>
                    ← Volver a administración
                </Button>

                <Typography variant="h1" gutterBottom>
                    Empresas Prixi
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Empresas shadow en Podscope con <code>prixiCompanyId</code>, podcasts vinculados y campañas
                    de reporting.
                </Typography>

                <Tabs
                    value={tab}
                    onChange={(_, v: 'companies' | 'campaigns') => setTab(v)}
                    sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab value="companies" label="Empresas" />
                    <Tab value="campaigns" label="Campañas" />
                </Tabs>

                {tab === 'companies' ? <CompaniesTab /> : <CampaignsTab />}
            </Box>
        </Container>
    );
};

export default AdminPrixiCompanies;
